/**
 * SeverityCell Component
 *
 * Displays a severity badge for vulnerability priorities.
 * Used in the SelectionTable vulnerability columns.
 */

import { cn } from '@/utils/classname';
import { getVulnerabilitySeverityLabel } from '@/utils/entities/vulnerability.util';

/**
 * Props for SeverityCell component
 */
export interface SeverityCellProps {
  /** Numeric priority value (0=Critical, 10=High, 20=Medium, 30=Low, 40=Info) */
  priority: number | undefined;
}

/**
 * Color mapping for severity levels
 */
const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500/10 text-red-600 ring-red-500/20',
  high: 'bg-orange-500/10 text-orange-600 ring-orange-500/20',
  medium: 'bg-yellow-500/10 text-yellow-600 ring-yellow-500/20',
  low: 'bg-blue-500/10 text-blue-600 ring-blue-500/20',
  info: 'bg-gray-500/10 text-gray-600 ring-gray-500/20',
};

/**
 * SeverityCell displays a colored badge based on vulnerability priority.
 *
 * @example
 * ```tsx
 * <SeverityCell priority={0} />  // Shows "Critical" badge
 * <SeverityCell priority={10} /> // Shows "High" badge
 * ```
 */
export function SeverityCell({ priority }: SeverityCellProps) {
  const label = getVulnerabilitySeverityLabel(priority ?? 40);
  const colorKey = label.toLowerCase();
  const colorClasses = SEVERITY_COLORS[colorKey] ?? SEVERITY_COLORS.info;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        'capitalize ring-1 ring-inset',
        colorClasses
      )}
    >
      {label}
    </span>
  );
}
